import Box from "@cloudscape-design/components/box"
import ExpandableSection from "@cloudscape-design/components/expandable-section"
import Icon from "@cloudscape-design/components/icon"
import Link from "@cloudscape-design/components/link"
import Popover from "@cloudscape-design/components/popover"
import SpaceBetween from "@cloudscape-design/components/space-between"

import { DQDL_RULES } from "./constants"
import { DqdlRule, RuleType } from "./types"

/**
 * Props for the DerivedRulesSection component.
 */
interface DerivedRulesSectionProps {
  /**
   * Read-only rules derived from the Metadata tab's transfer_metadata_schema.
   */
  derivedRules: DqdlRule[]

  /**
   * Callback to navigate to the Metadata tab.
   */
  onNavigateToMetadata?: () => void
}

/**
 * Maps a ColumnDataType Spark target to the declared Format wording shown to the user.
 */
const DATA_TYPE_LABELS: Record<string, string> = {
  DOUBLE: "a number",
  DATE: "a date",
  TIMESTAMP: "a datetime",
}

/**
 * Returns a human-readable description of a DQDL rule.
 */
const getRuleDescription = (rule: DqdlRule): string => {
  switch (rule.type) {
    case "IsComplete":
      return `\`${rule.column}\` must not be empty`
    case "IsPrimaryKey":
      return `\`${(rule.columns || []).join(", ")}\` must be unique`
    case "ColumnLength":
      return `\`${rule.column}\` length must be ≤ ${rule.max}`
    case "ColumnDataType":
      return `\`${rule.column}\` must be ${DATA_TYPE_LABELS[rule.dataType ?? ""] ?? rule.dataType}`
    default:
      return rule.type
  }
}

/**
 * Returns the source label and tooltip for a derived rule based on its type.
 */
const getSourceInfo = (type: RuleType): { label: string; tooltip: string } => {
  switch (type) {
    case "IsPrimaryKey":
      return {
        label: "Unique Sample Assessment Identifier is checked",
        tooltip:
          'Columns marked as "Unique Sample Assessment Identifier" in the Metadata tab must be unique',
      }
    case "IsComplete":
      return {
        label: "Populate For All Records is checked",
        tooltip: 'Columns marked as "Populate For All Records" in the Metadata tab cannot be empty',
      }
    case "ColumnLength":
      return {
        label: "Max Length is specified",
        tooltip: "Columns with a Max Length in the Metadata tab cannot exceed that length",
      }
    case "ColumnDataType":
      return {
        label: "Data type is numeric, Date, or Datetime",
        tooltip:
          "Columns with a numeric (Integer/Decimal/Double), Date, or Datetime data type in the Metadata tab must hold values that match the declared type",
      }
    default:
      return { label: "Metadata", tooltip: "" }
  }
}

/**
 * Renders a rule description, wrapping any backtick-delimited segments as inline code.
 */
const renderDescription = (description: string): React.ReactNode =>
  description.split("`").map((part, i) =>
    i % 2 === 1 ? (
      <Box key={i} variant="code" display="inline">
        {part}
      </Box>
    ) : (
      part
    ),
  )

/**
 * Displays read-only DQDL rules derived from the Metadata tab. These rules are automatically synced
 * and cannot be edited directly. Collapsed by default to reduce visual clutter.
 */
export const DerivedRulesSection: React.FC<DerivedRulesSectionProps> = ({
  derivedRules,
  onNavigateToMetadata,
}) => {
  const allRules = derivedRules

  if (allRules.length === 0) {
    return null
  }

  return (
    <ExpandableSection
      variant="container"
      headerText={`Derived from Metadata (${allRules.length} rule${allRules.length !== 1 ? "s" : ""})`}
    >
      <SpaceBetween size="s">
        <Box color="text-body-secondary">
          These rules are automatically synced from the{" "}
          {onNavigateToMetadata ? (
            <Link variant="primary" onFollow={onNavigateToMetadata}>
              Metadata tab
            </Link>
          ) : (
            "Metadata tab"
          )}{" "}
          and cannot be edited here.
        </Box>

        {allRules.map((rule, index) => {
          const sourceInfo = getSourceInfo(rule.type)
          return (
            <Box key={index} padding="s">
              <SpaceBetween size="xxs">
                <SpaceBetween size="xs" direction="horizontal">
                  <Box variant="strong">{DQDL_RULES[rule.type]?.label || rule.type}</Box>
                  {sourceInfo.tooltip && (
                    <Popover
                      triggerType="custom"
                      dismissButton={false}
                      position="top"
                      size="small"
                      content={sourceInfo.tooltip}
                    >
                      <Icon name="status-info" />
                    </Popover>
                  )}
                </SpaceBetween>
                <Box variant="small">{renderDescription(getRuleDescription(rule))}</Box>
              </SpaceBetween>
            </Box>
          )
        })}
      </SpaceBetween>
    </ExpandableSection>
  )
}
